type StorageLike = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
    clear(): void;
};

class MemoryStorage implements StorageLike {
    #store = new Map<string, string>();

    getItem(key: string) {
        return this.#store.has(key) ? this.#store.get(key)! : null;
    }

    setItem(key: string, value: string) {
        this.#store.set(key, value);
    }

    removeItem(key: string) {
        this.#store.delete(key);
    }

    clear() {
        this.#store.clear();
    }
}

const isBrowser = typeof window !== 'undefined';

const getBrowserStorage = (name: 'localStorage' | 'sessionStorage'): StorageLike | null => {
    if (!isBrowser) return null;
    try {
        const storage = window[name];
        const key = '__safe_storage_test__';
        storage.setItem(key, key);
        storage.removeItem(key);
        return storage;
    } catch {
        return null;
    }
};

const resolveStorage = (storage?: StorageLike | null, fallback?: StorageLike) => storage ?? fallback ?? null;

export const createMemoryStorage = () => new MemoryStorage();

export const createSafeStorage = ({
    storage,
    fallback,
}: {
    storage?: StorageLike | null;
    fallback?: StorageLike;
} = {}) => {
    const memory = fallback ?? createMemoryStorage();
    let active = resolveStorage(storage, memory) ?? memory;

    const ensureActive = () => {
        if (!active) {
            active = memory;
        }
        return active;
    };

    const handleFailure = () => {
        active = memory;
        return active;
    };

    return {
        getItem(key: string) {
            try {
                return ensureActive().getItem(key);
            } catch {
                return handleFailure().getItem(key);
            }
        },
        setItem(key: string, value: string) {
            try {
                ensureActive().setItem(key, value);
            } catch {
                handleFailure().setItem(key, value);
            }
        },
        removeItem(key: string) {
            try {
                ensureActive().removeItem(key);
            } catch {
                handleFailure().removeItem(key);
            }
        },
        clear() {
            try {
                ensureActive().clear();
            } catch {
                handleFailure().clear();
            }
        },
    } satisfies StorageLike;
};

export const safeLocalStorage = createSafeStorage({
    storage: getBrowserStorage('localStorage'),
});

export const safeSessionStorage = createSafeStorage({
    storage: getBrowserStorage('sessionStorage'),
});

export type { StorageLike };
